<template lang="pug">
    section.articles-step
        el-form-item.hidden(prop="items")
        .find-article-panel
            el-autocomplete.is-full-width(
                v-model="search"
                value-key="name"
                :fetch-suggestions="autocomplete"
                :placeholder="$t('Find product...')"
                @select="selectProduct"
                @keydown.escape.native="resetSearch"
                @keydown.enter.native.prevent
                @clear="resetSearch"
            )
                template(slot="prefix")
                    i.fas.el-icon-fa-magnifying-glass
                template(slot-scope="{ item }")
                    product-preview(
                        :product="item"
                        :customer-group="customerGroup"
                        :price-type="priceType"
                        :currency="currency"
                    )

        .shopping-cart
            transition-group(name="slide-decent-vertical")
                shopping-cart-item(
                    v-for="item in form.items"
                    :key="item.id"
                    :item="item"
                    :customer="form.purchaser.customer"
                    :price-type="priceType"
                    :currency="{ code: currency, sign: currencySign }"
                    @delete="form.items = form.items.filter(el => el.id !== item.id)"
                )

        el-dialog(
            :title="$t('Configure product')"
            :visible.sync="dialog.visibility"
            :before-close="confirmDiscard(() => changedSince(dialog.formSnapshot, dialog.form))"
            :close-on-click-modal="false"
            top="10vh"
            width="800px"
            @closed="resetDialog"
        )
            .drawer-body(v-loading="dialog.loading")
                el-form(
                    v-if="dialog.product && dialog.articles"
                    label-position="top"
                    :model="dialog.form"
                    :rules="dialog.rules"
                    ref="drawer-form"
                )
                    product-form(
                        :key="dialog.product.id"
                        :product="dialog.product"
                        :articles="dialog.articles"
                        :currency="currency"
                        :price-type="priceType"
                        :form="dialog.form"
                        :rules="dialog.rules"
                    )
            template(slot="footer")
                .modal-footer
                    .left
                        el-button(
                            size="mini"
                            icon="el-icon-fa-times fas"
                            :disabled="dialog.loading"
                            @click="dialog.visibility = false"
                        ) {{ $t('Cancel') }}
                    .right
                        el-button(
                            size="mini"
                            type="primary"
                            icon="el-icon-fa-check fas"
                            :disabled="dialog.loading"
                            @click="addArticle"
                        ) {{ $t('Ok') }}
</template>

<i18n>
{
    "de": {
        "Find product...": "Artikel finden...",
        "Configure product": "Artikel konfigurieren"
    }
}
</i18n>

<script>
import _ from 'lodash';
import axios from 'axios';
import uniqid from 'uniqid';

import pinia from '@/pinia';
import { useSetupStore, useShopProductStore } from '@/stores';

import ProductApi from '@/extensions/shop/store/api/product';
import ArticleApi from '@/extensions/shop/store/api/article';

import ProductPreview from './Partials/ProductPreview';
import ProductForm from './Partials/ProductForm';
import discardGuard from '@/mixins/discardGuard';
import ShoppingCartItem from './Partials/ShoppingCartItem';

export default {
    name: 'ArticlesStep',
    mixins: [discardGuard],
    props: {
        form: {
            type: Object,
            required: true
        },
        rules: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            search: '',
            dialog: {
                visibility: false,
                loading: false,
                error: false,
                product: undefined,
                articles: undefined,
                form: {
                    qty: 1,
                    article: undefined,
                    price: undefined,
                    sale_price: undefined,
                    propertyValueOptions: [],
                    properties: [],
                },
                formSnapshot: undefined,
                rules: {
                    article: { id: 'required' },
                    propertyValueOptions: [],
                    properties: [],
                },
            }
        };
    },
    components: {
        ProductForm,
        ProductPreview,
        ShoppingCartItem,
    },
    computed: {
        setupStore() {
            return useSetupStore(pinia);
        },
        shopProductStore() {
            return useShopProductStore(pinia);
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        meilisearchTenantToken() {
            const key = 'meilisearch-tenant-token'
            const apiKeys = this.activeCms.apiKeys || []

            const meilisearchTenantToken = apiKeys.find(el => el.key === key)
            return _.get(meilisearchTenantToken, 'public')
        },
        priceType() {
            const customer = this.form.purchaser.customer

            let priceType = 'gross'
            if (customer && customer.show_net_prices) priceType = 'net'

            return priceType
        },
        customerGroup() {
            const customer = this.form.purchaser.customer
            return _.get(customer, 'customerGroup') || _.get(this.activeCms, 'shop.defaultCustomerGroup')
        },
        currency() {
            return _.get(this.customerGroup, 'currency.iso4217') || 'EUR'
        },
        currencySign() {
            return _.get(this.customerGroup, 'currency.sign') || '€'
        },
        options() {
            return this.shopProductStore.options;
        },
    },
    methods: {
        async autocomplete(query, callback) {
            if (!query) return callback([])

            if (!this.meilisearchTenantToken) {
                // TODO: fetch from api

                callback([])
            } else {
                const endpoint = process.env.VUE_APP_MEILISEARCH_ENDPOINT;
                const index = process.env.VUE_APP_MEILISEARCH_PRODUCTS_INDEX_UID;

                const config = {
                    headers: {
                        Authorization: `Bearer ${this.meilisearchTenantToken}`
                    }
                };

                const customer = this.form.purchaser.customer

                let filter = `status = 'enabled'`;

                let restrictionFilter = 'restriction NOT EXISTS';
                if (customer) {
                    restrictionFilter += ` OR restriction = 'members'`;
                    const memberTags = customer.member.memberTags || [];
                    let memberTagIDs = memberTags.map(tag => tag.id);
                if (memberTagIDs.length) {
                        restrictionFilter += ` OR (restriction = 'member-tags' AND member_tags.id IN [${memberTagIDs.join(',')}])`;
                    }
                } else {
                    restrictionFilter += ` OR restriction = 'guests'`;
                }

                filter += ` AND (${restrictionFilter})`

                if (this.customerGroup) {
                    filter += ` AND filterables.prices.${this.priceType}.${this.customerGroup.key} EXISTS`;
                    // filter += ` AND sellable.${this.customerGroup.key} = true`;
                } else {
                    // filter += ` AND sellable.guests = true`;
                }

                const { data, status } = await axios
                    .post(`${endpoint}/indexes/${index}/search`, { q: query, limit: 80, filter }, config)
                    .catch(err => {
                        console.error(err)
                        this.$notify.error({
                            title: this.$t('An error occurred!'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    });

                if (status !== 200) {
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });

                    return callback([]);
                }

                callback(data.hits)
            }
        },
        selectProduct(product) {
            this.search = ''

            if (product.article && !(product.properties || []).find(el => el.type === 'input' || _.get(el, 'values.length', 0) > 1)) {
                // add product to cart

                const i = this.form.items.findIndex(item => parseInt(item.article.id) === parseInt(product.article.id))
                if (i >= 0) {
                    this.form.items[i].qty++
                } else {
                    const productPrices = _.get(product, 'prices') || []
                    const articlePrices = _.get(product.article, 'prices') || []

                    let price = articlePrices.find(price => price.type === 'main')
                        || productPrices.find(price => price.type === 'main')

                    let salePrice
                    if (this.customerGroup && this.customerGroup.id != _.get(this.activeCms, 'shop.defaultCustomerGroup.id')) {
                        price = articlePrices.find(price => {
                            return price.type === 'group' && parseInt(price.customer_group_id) === parseInt(this.customerGroup.id)
                        }) || productPrices.find(price => {
                            return price.type === 'group' && parseInt(price.customer_group_id) === parseInt(this.customerGroup.id)
                        })

                        salePrice = articlePrices.find(price => {
                            return price.type === 'sale' && parseInt(price.customer_group_id) === parseInt(this.customerGroup.id)
                        }) || productPrices.find(price => {
                            return price.type === 'sale' && parseInt(price.customer_group_id) === parseInt(this.customerGroup.id)
                        })
                    }

                    let surcharges = 0
                    let propertyValueOptions = []
                    if (product.properties && product.properties.length) {
                        product.properties.forEach(property => {
                            const values = _.get(property, 'values')
                            if (values && values.length === 1) {
                                const value = values[0]

                                const prices = _.get(value, 'prices') || []
                                const price = prices.find(price => parseInt(price.customer_group_id) === parseInt(this.customerGroup.id))

                                let surcharge
                                if (price) {
                                    surcharge = _.get(price, `${this.priceType}_value`)
                                    surcharges += surcharge
                                }

                                propertyValueOptions.push({
                                    id: value.id,
                                })
                            }
                        })
                    }

                    this.form.items.push({
                        id: uniqid('new:'),
                        article: { id: product.article.id },
                        price: _.get(price, `${this.priceType}_value`) + surcharges,
                        sale_price: _.get(salePrice, `${this.priceType}_value`) ? salePrice[`${this.priceType}_value`] + surcharges : undefined,
                        properties: [],
                        propertyValueOptions,
                        qty: 1,
                    })
                }
            } else {
                // open dialog

                this.dialog.visibility = true
                this.dialog.loading = true

                let variables = {
                    // id: product.id,
                    shop: parseInt(this.activeCms.shop.id),
                }

                if (this.form.purchaser.customer) {
                    variables.mimicCustomer = {
                        id: this.form.purchaser.customer.id,
                        customerGroup: { id: this.customerGroup.id },
                    }
                }

                const fetchProduct = ProductApi.fetchProduct({ id: product.id, ...variables })
                    .then(product => {
                        this.dialog.product = product
                    })
                    .catch(error => {
                        console.error(error)
                    })

                const fetchArticles = ArticleApi.fetchArticles({ product: product.id, ...variables })
                    .then(articles => {
                        this.dialog.articles = articles
                        if (articles.length === 1) {
                            this.dialog.form.article = articles[0]
                        }
                    })
                    .catch(error => {
                        console.error(error)
                    })

                Promise.all([fetchProduct, fetchArticles])
                    .finally(() => {
                        this.dialog.loading = false
                        // the product form fills in the preselected article and its prices first
                        this.$nextTick(() => {
                            this.dialog.formSnapshot = this.snapshotOf(this.dialog.form)
                        })
                    })
            }
        },
        addArticle() {
            this.$refs['drawer-form'].validate((valid, errors) => {
                if (valid) {
                    const item = _.cloneDeep(this.dialog.form)
                    item.price = _.round(item.price, 2)

                    const i = this.form.items.findIndex(el => {
                        const sameProperties = _.isEqual(el.properties, item.properties)
                        const samePropertyValueOptions = _.isEqual(el.propertyValueOptions, item.propertyValueOptions)
                        return el.article.id === item.article.id && sameProperties && samePropertyValueOptions
                    })

                    if (i >= 0) {
                        this.form.items[i].qty++
                    } else {
                        item.id = uniqid('new:')
                        // this.form.items.push(item)
                        this.form.items.unshift(item)
                    }

                    this.resetDialog()
                } else {
                    this.$notify.warning({
                        title: this.$t('Form is not valid!'),
                        message: this.$t('Correct your input and submit again.'),
                        iconClass: 'fas el-icon-fa-exclamation-circle',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                }
            })
        },
        resetDialog() {
            this.dialog.visibility = false
            this.dialog.loading = false
            this.dialog.error = false
            this.dialog.product = undefined
            this.dialog.articles = undefined
            this.dialog.formSnapshot = undefined

            this.dialog.form = {
                qty: 1,
                article: undefined,
                price: undefined,
                sale_price: undefined,
                propertyValueOptions: [],
                properties: [],
            }
        },
        resetSearch() {
            this.search = ''
        },
    }
};
</script>

<style lang="sass" scoped>
.find-article-panel
    background: $color-skeleton
    padding: 2rem
    border-radius: 4px
    display: flex
    align-items: center
    justify-content: center
    margin-bottom: 4px

.shopping-cart-row + .shopping-cart-row
    margin-top: 4px

.el-autocomplete::v-deep
    max-width: 600px
    .el-input__prefix
        display: flex
        justify-content: center
        align-items: center
        width: 24px
</style>
